import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Linking,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { useColors, ThemeColors } from "../hooks/useColors";
import { spacing, typography } from "../theme";

export default function PaymentProcessingInfoScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation();

  const handleStripeLearnMore = () => {
    Linking.openURL("https://stripe.com/connect/express-accounts");
  };

  const handleSupportContact = () => {
    // You can customize this with your support email or chat system
    Linking.openURL(
      "mailto:support@jadetrade.com?subject=Payment Processing Question"
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={["bottom", "top"]}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Feather name="arrow-left" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Payment Processing</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Overview Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>How Payment Processing Works</Text>
          <Text style={styles.bodyText}>
            JadeTrade uses Stripe to process all payments securely.
            When a customer purchases your items, Stripe handles the payment
            processing and holds the funds temporarily before releasing them to
            your account.
          </Text>
        </View>

        {/* When You Get Paid */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>When Do I Receive Payments?</Text>
          <View style={styles.bulletPoint}>
            <Feather name="clock" size={16} color={colors.primary} />
            <Text style={styles.bulletText}>
              <Text style={styles.boldText}>Standard Timeline:</Text> Payments
              are typically available in your bank account 2-7 business days
              after the customer's order is marked as delivered.
            </Text>
          </View>
          <View style={styles.bulletPoint}>
            <Feather name="shield" size={16} color={colors.primary} />
            <Text style={styles.bulletText}>
              <Text style={styles.boldText}>Security Hold:</Text> For new
              sellers, Stripe may hold payments for up to 14 days as a security
              measure to prevent fraud.
            </Text>
          </View>
          <View style={styles.bulletPoint}>
            <Feather name="check-circle" size={16} color={colors.primary} />
            <Text style={styles.bulletText}>
              <Text style={styles.boldText}>After Delivery:</Text> Once your
              customer confirms delivery or the delivery tracking shows
              completion, your payment is released.
            </Text>
          </View>
        </View>

        {/* Stripe Hold Policies */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            Understanding Stripe's Hold Policies
          </Text>
          <Text style={styles.bodyText}>
            Stripe implements holding periods to protect both buyers and
            sellers:
          </Text>
          <View style={styles.bulletPoint}>
            <Feather name="user-check" size={16} color={colors.primary} />
            <Text style={styles.bulletText}>
              <Text style={styles.boldText}>New Sellers:</Text> Initial holds of
              7-14 days until your account builds a transaction history.
            </Text>
          </View>
          <View style={styles.bulletPoint}>
            <Feather name="trending-up" size={16} color={colors.primary} />
            <Text style={styles.bulletText}>
              <Text style={styles.boldText}>Established Sellers:</Text> Reduced
              holds of 2-7 days once you've successfully completed several
              transactions.
            </Text>
          </View>
          <View style={styles.bulletPoint}>
            <Feather name="alert-circle" size={16} color={colors.primary} />
            <Text style={styles.bulletText}>
              <Text style={styles.boldText}>High-Risk Transactions:</Text>{" "}
              Larger orders or unusual patterns may trigger extended holds for
              security.
            </Text>
          </View>
        </View>

        {/* Fee Structure */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Fee Structure</Text>
          <View style={styles.feeCard}>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>Seller Processing Fee:</Text>
              <Text style={styles.feeValue}>10%</Text>
            </View>
            <View style={styles.feeRow}>
              <Text style={styles.feeLabel}>Buyer Processing Fee:</Text>
              <Text style={styles.feeValue}>15%</Text>
            </View>
            <Text style={styles.feeNote}>
              Processing fees are deducted automatically when payments are
              processed. These fees cover Stripe's payment processing, fraud
              protection, and platform maintenance.
            </Text>
          </View>
        </View>

        {/* FAQ Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Frequently Asked Questions</Text>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Why are my payments held?</Text>
            <Text style={styles.faqAnswer}>
              Payment holds protect against fraud and chargebacks. New sellers
              typically experience longer holds until they establish a positive
              transaction history.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I speed up my payments?</Text>
            <Text style={styles.faqAnswer}>
              Payment timing is controlled by Stripe's algorithms. Consistent
              positive reviews, prompt shipping, and completed deliveries help
              build trust and may reduce hold periods over time.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What if there's a dispute?</Text>
            <Text style={styles.faqAnswer}>
              If a customer files a dispute, Stripe will temporarily hold the
              disputed amount until the issue is resolved. Maintain good records
              and communication to resolve disputes quickly.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How do I track my payments?</Text>
            <Text style={styles.faqAnswer}>
              You can view payment status in your Stripe Express dashboard.
              Access this through "Manage on Stripe" in your seller settings.
            </Text>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionSection}>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleStripeLearnMore}
          >
            <Feather name="external-link" size={16} color={colors.surface} />
            <Text style={styles.primaryButtonText}>
              Learn More About Stripe
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={handleSupportContact}
          >
            <Feather name="mail" size={16} color={colors.primary} />
            <Text style={styles.secondaryButtonText}>Contact Support</Text>
          </TouchableOpacity>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    backButton: {
      padding: spacing.xs,
      marginRight: spacing.sm,
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
    },
    content: {
      flex: 1,
      padding: spacing.md,
    },
    section: {
      marginBottom: spacing.lg,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    bodyText: {
      fontSize: 14,
      lineHeight: 20,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    bulletPoint: {
      flexDirection: "row",
      alignItems: "flex-start",
      marginBottom: spacing.sm,
      paddingLeft: spacing.xs,
    },
    bulletText: {
      fontSize: 14,
      lineHeight: 20,
      color: colors.text,
      marginLeft: spacing.sm,
      flex: 1,
    },
    boldText: {
      fontWeight: "600",
      color: colors.text,
    },
    feeCard: {
      backgroundColor: colors.backgroundSecondary,
      padding: spacing.md,
      borderRadius: 8,
      borderLeftWidth: 3,
      borderLeftColor: colors.primary,
    },
    feeRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    feeLabel: {
      fontSize: 14,
      color: colors.text,
    },
    feeValue: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.primary,
    },
    feeNote: {
      fontSize: 12,
      color: colors.textLight,
      fontStyle: "italic",
      marginTop: spacing.sm,
    },
    faqItem: {
      marginBottom: spacing.md,
      paddingBottom: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 14,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.xs,
    },
    faqAnswer: {
      fontSize: 14,
      lineHeight: 18,
      color: colors.textLight,
    },
    actionSection: {
      marginTop: spacing.lg,
    },
    primaryButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.primary,
      padding: spacing.md,
      borderRadius: 8,
      marginBottom: spacing.sm,
    },
    primaryButtonText: {
      color: colors.surface,
      fontWeight: "600",
      marginLeft: spacing.xs,
    },
    secondaryButton: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.md,
      borderRadius: 8,
    },
    secondaryButtonText: {
      color: colors.primary,
      fontWeight: "600",
      marginLeft: spacing.xs,
    },
  });
